// La marge des chantiers, mois par mois — le raisonnement, pur et testé à part.
//
// Ce que l'écran doit dire : ce mois-ci, les chantiers ont rapporté tant (HT),
// les achats ont coûté tant, il reste tant. Rien de plus, mais juste.
//
// DEUX CHOSES À SAVOIR AVANT DE LIRE CES CHIFFRES :
//
//   1. Les achats sont saisis comme un MONTANT UNIQUE par chantier (champ
//      « Achats fournisseurs » de la fiche). Le CRM ne porte pas de factures
//      fournisseurs datées : on ne peut donc pas répartir les achats d'un
//      chantier sur plusieurs mois. Un chantier est rattaché à UN SEUL mois,
//      avec la totalité de ses achats — sinon la marge d'un mois serait un
//      mélange de recettes d'un mois et de dépenses d'un autre.
//
//   2. Le mois d'un chantier est celui de sa FACTURATION : le mois de sa
//      dernière facture émise. C'est le mois où l'argent a été réclamé, celui
//      que la comptabilité reconnaît. Un chantier pas encore facturé se range
//      au mois de sa fin de travaux prévue, sur le montant du devis signé —
//      c'est une prévision, et l'écran doit le dire.

/** Une facture qui a été ÉMISE n'est ni un brouillon ni une facture annulée. */
const STATUTS_NON_EMIS = new Set(['brouillon', 'annulee_avoir']);

/** Un chantier à ces statuts-là n'a pas de marge à montrer. */
const STATUTS_HORS_MARGE = new Set(['prospect', 'annule']);

const MOIS_LONGS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

export interface ChantierMarge {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  chefChantierId: string | null;
  commercialId: string | null;
  /** Ce que les fournisseurs ont coûté sur ce chantier, hors taxes. */
  achats: number;
  /** Le montant vendu, saisi sur la fiche : sert tant que rien n'est facturé. */
  montantVenteHt: number;
  /** Fin de travaux prévue, `AAAA-MM-JJ`. Rattache un chantier non facturé. */
  datePrevueFin: string | null;
  datePrevueDebut: string | null;
}

export interface FactureMarge {
  id: string;
  chantierId: string;
  /** acompte | situation | finale | avoir | standard */
  kind: string;
  statut: string;
  montantHt: number;
  /** Jour d'émission, `AAAA-MM-JJ`. */
  dateEmission: string;
  /** Pour un avoir : la facture qu'il annule. */
  factureLieeId: string | null;
}

/** D'où vient le chiffre d'affaires d'une ligne — l'écran ne doit pas mentir. */
export type SourceCa = 'facture' | 'devis';

export interface LigneMarge {
  chantierId: string;
  numero: string;
  libelle: string;
  statut: string;
  chefChantierId: string | null;
  commercialId: string | null;
  caHt: number;
  achats: number;
  marge: number;
  /** Marge / CA. Null quand le CA est nul : un taux n'aurait aucun sens. */
  tauxMarge: number | null;
  source: SourceCa;
}

export interface MoisMarge {
  /** `AAAA-MM`, pour trier et comparer. */
  cle: string;
  /** « septembre 2026 », pour l'afficher. */
  label: string;
  lignes: LigneMarge[];
  caHt: number;
  achats: number;
  marge: number;
  tauxMarge: number | null;
}

export interface ResultatMarges {
  /** Les mois qui portent quelque chose, du plus récent au plus ancien. */
  mois: MoisMarge[];
  /**
   * Les chantiers qu'aucune date ne rattache à un mois : ni facture émise, ni
   * date de travaux. Ils ne sont pas perdus — l'écran les signale, pour qu'on
   * aille renseigner ce qui manque.
   */
  sansMois: LigneMarge[];
}

function taux(marge: number, caHt: number): number | null {
  return caHt > 0 ? marge / caHt : null;
}

export function libelleMoisLong(cle: string): string {
  const mois = MOIS_LONGS[Number(cle.slice(5, 7)) - 1];
  return mois === undefined ? cle : `${mois} ${cle.slice(0, 4)}`;
}

export function margesMensuelles(entree: {
  chantiers: ChantierMarge[];
  factures: FactureMarge[];
}): ResultatMarges {
  const { chantiers, factures } = entree;

  // Émettre un avoir fait passer la facture d'origine en `annulee_avoir` : les
  // deux s'effacent ensemble. Un avoir dont la facture est déjà sortie du
  // calcul n'a donc plus rien à retrancher.
  const comptees = new Set(
    factures.filter((f) => !STATUTS_NON_EMIS.has(f.statut)).map((f) => f.id),
  );
  const emises = factures.filter(
    (f) =>
      !STATUTS_NON_EMIS.has(f.statut) &&
      (f.kind !== 'avoir' || f.factureLieeId === null || comptees.has(f.factureLieeId)),
  );

  const parChantier = new Map<string, FactureMarge[]>();
  for (const f of emises) {
    const lot = parChantier.get(f.chantierId);
    if (lot === undefined) parChantier.set(f.chantierId, [f]);
    else lot.push(f);
  }

  const parMois = new Map<string, LigneMarge[]>();
  const sansMois: LigneMarge[] = [];

  for (const ch of chantiers) {
    if (STATUTS_HORS_MARGE.has(ch.statut)) continue;

    const sesFactures = parChantier.get(ch.id) ?? [];
    const caFacture = sesFactures.reduce(
      (s, f) => s + (f.kind === 'avoir' ? -f.montantHt : f.montantHt),
      0,
    );
    // Un avoir ne déplace pas le chantier : il retire de l'argent, il ne dit
    // pas quand les travaux ont été vendus.
    const emissions = sesFactures.filter((f) => f.kind !== 'avoir').map((f) => f.dateEmission);
    const derniereEmission =
      emissions.length === 0 ? null : emissions.reduce((a, b) => (a > b ? a : b));

    const facture = derniereEmission !== null;
    const caHt = facture ? caFacture : ch.montantVenteHt;
    const achats = ch.achats;

    // Ni argent ni dépense : la ligne n'apprendrait rien à personne.
    if (!facture && caHt === 0 && achats === 0) continue;

    const ligne: LigneMarge = {
      chantierId: ch.id,
      numero: ch.numero,
      libelle: ch.libelle,
      statut: ch.statut,
      chefChantierId: ch.chefChantierId,
      commercialId: ch.commercialId,
      caHt,
      achats,
      marge: caHt - achats,
      tauxMarge: taux(caHt - achats, caHt),
      source: facture ? 'facture' : 'devis',
    };

    const jour = derniereEmission ?? ch.datePrevueFin ?? ch.datePrevueDebut;
    if (jour === null) {
      sansMois.push(ligne);
      continue;
    }
    const cle = jour.slice(0, 7);
    const lot = parMois.get(cle);
    if (lot === undefined) parMois.set(cle, [ligne]);
    else lot.push(ligne);
  }

  const mois = [...parMois.entries()]
    .map(([cle, lignes]) => {
      const caHt = lignes.reduce((s, l) => s + l.caHt, 0);
      const achats = lignes.reduce((s, l) => s + l.achats, 0);
      return {
        cle,
        label: libelleMoisLong(cle),
        // Le plus gros chantier en haut : c'est là que se joue le mois.
        lignes: [...lignes].sort((a, b) => b.caHt - a.caHt),
        caHt,
        achats,
        marge: caHt - achats,
        tauxMarge: taux(caHt - achats, caHt),
      };
    })
    // Du plus récent au plus ancien : le mois en cours s'ouvre en premier.
    .sort((a, b) => b.cle.localeCompare(a.cle));

  return { mois, sansMois: sansMois.sort((a, b) => b.caHt - a.caHt) };
}
